import type { ConfigApi, InstanceApi } from '@eis/insuranceservice-sdk';
import { isApiError } from './result';

/**
 * Reading a refused publish.
 *
 * The gate does not fail with a message (service summary §3.4). It fails with a REPORT — the
 * `{goldenCaseId, expected, actual}` triples of every golden case that repriced, the contract
 * issues, the smoke failures — because "the premium moved" is a decision an actuary has to make
 * and a sentence gives them nothing to make it with.
 *
 * That report arrives as a typed status detail, so the shape is the gateway's error envelope
 * rather than anything the console designed:
 *
 *   { code, message, details: [ {"@type": ".../google.rpc.ErrorInfo", reason: "…"},
 *                               {"@type": ".../insurance.config.v1.GateReport", …} ] }
 *
 * Two functions rather than one `isGateFailure`, because the reason and the report answer
 * different questions: the reason decides WHICH screen (a repriced golden case is not a broken
 * pin), the report is what that screen renders.
 */

/** `PUBLISH_GATE_FAILED` — a golden case repriced under a release type that promised it would not. */
export const REASON_GATE_FAILED = 'PUBLISH_GATE_FAILED';

/** `REF_CONFLICT` — the manifest pinned something unpublished, missing, or another product's. */
export const REASON_REF_CONFLICT = 'REF_CONFLICT';

/**
 * `ASSET_NOT_EDITABLE` — a write reached a PUBLISHED or DISCARDED asset (SDD-010 R-14). A 409
 * that is NOT a race: re-reading changes nothing, the fix is a new draft based on this one.
 */
export const REASON_ASSET_NOT_EDITABLE = 'ASSET_NOT_EDITABLE';

const ERROR_INFO_TYPE = 'google.rpc.ErrorInfo';
const GATE_REPORT_TYPE = 'insurance.config.v1.GateReport';
const VALIDATION_REPORT_TYPE = 'insurance.config.v1.ValidationReport';
const BAD_REQUEST_TYPE = 'google.rpc.BadRequest';
const UPGRADE_CHECK_TYPE = 'insurance.instance.v1.UpgradeCheckResult';

function details(error: unknown): readonly Record<string, unknown>[] {
  if (!isApiError(error)) return [];
  const body: unknown = error.body;
  if (typeof body !== 'object' || body === null) return [];
  const list: unknown = (body as { details?: unknown }).details;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null,
  );
}

/**
 * Matches on the type NAME rather than the whole URL: the `type.googleapis.com/` prefix is a
 * convention of the marshaller, not part of the contract, and a proxy that rewrote it would
 * otherwise silently turn a structured refusal into an unexplained 422.
 */
function detailOfType(error: unknown, typeName: string): Record<string, unknown> | undefined {
  return details(error).find((entry) => {
    const type: unknown = entry['@type'];
    return typeof type === 'string' && (type === typeName || type.endsWith(`/${typeName}`));
  });
}

/** The machine-readable reason a service refused, or undefined when it named none. */
export function refusalReasonOf(error: unknown): string | undefined {
  const info = detailOfType(error, ERROR_INFO_TYPE);
  const reason: unknown = info?.reason;
  return typeof reason === 'string' ? reason : undefined;
}

/**
 * The ErrorInfo's metadata — the values a refusal carries beside its reason (the signature of an
 * infected upload, SDD-023 V-3). Empty when the service named none; string values only.
 */
export function refusalMetadataOf(error: unknown): Readonly<Record<string, string>> {
  const metadata: unknown = detailOfType(error, ERROR_INFO_TYPE)?.metadata;
  if (typeof metadata !== 'object' || metadata === null) return {};
  return Object.fromEntries(
    Object.entries(metadata).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
}

/**
 * The gate report carried by a refused publish or validate.
 *
 * Returns undefined when the error is anything else — a 500, a network failure, a conflict —
 * so a caller cannot accidentally render "the gate passed" for a request that never reached it.
 */
export function gateReportOf(error: unknown): ConfigApi.GateReport | undefined {
  const report = detailOfType(error, GATE_REPORT_TYPE);
  if (report === undefined) return undefined;
  // The detail IS the message, plus `@type`. Everything the SDK model declares is optional, so
  // this asserts nothing the wire has not already promised.
  return report;
}

/**
 * The validation report carried by a refused CORE-CONFIG publish (SDD-010 §6).
 *
 * A different message from the tarif-version {@link gateReportOf}: a core config is refused per
 * ITEM — a formula naming a later item, an enum without a lookup, a factor key no range holds —
 * each with a JSON Pointer into the document and, for an expression, a position in it. The same
 * `ValidationReport` a save returns in its body (C-18: a draft is saved WITH its errors), so an
 * editor renders one shape whether the report came back as data or as a refusal.
 */
export function validationReportOf(error: unknown): ConfigApi.ValidationReport | undefined {
  return detailOfType(error, VALIDATION_REPORT_TYPE);
}

/**
 * The `{path, code, message}` triples a service attaches when it refuses a DOCUMENT.
 *
 * Not the same thing as a gate report, and kept apart for the reason the two reasons are: a
 * refused manifest is a repriced premium an actuary signs off on, while a refused asset document
 * is a field somebody has to fix. They arrive in different details — `GateReport.contractIssues`
 * against a publish, `google.rpc.BadRequest.fieldViolations` against a save — and the screens
 * that render them are different screens.
 *
 * Both spellings are read, because both are on the wire: the gateway maps a validation refusal to
 * `BadRequest` with `field` and `description`, and the config API's own `ValidationIssue` carries
 * `path`, `code` and `message`. Normalising here rather than in the editor is what keeps the
 * editor's field mapping about JSON Pointers instead of about which envelope it came from.
 */
export function validationIssuesOf(error: unknown): ConfigApi.ValidationIssue[] {
  const detail = detailOfType(error, BAD_REQUEST_TYPE);
  const violations: unknown = detail?.fieldViolations;
  if (!Array.isArray(violations)) return [];
  return violations
    .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
    .map((entry) => ({
      path: stringOf(entry.path ?? entry.field),
      code: stringOf(entry.code ?? entry.reason),
      message: stringOf(entry.message ?? entry.description),
    }));
}

const stringOf = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The reason a refused upgrade carries (SDD-016 §5): the instance does not fit the target. */
export const REASON_UPGRADE_CHECK_FAILED = 'UPGRADE_CHECK_FAILED';

/**
 * The check result a refused upgrade carries as a typed status detail — every failed check with
 * its path — or undefined for any other error, so a caller cannot render "the check passed" for a
 * request that never reached it.
 */
export function upgradeRefusalOf(error: unknown): InstanceApi.UpgradeCheckResult | undefined {
  // The detail IS the message, plus `@type`; every field the SDK model declares is optional.
  return detailOfType(error, UPGRADE_CHECK_TYPE);
}
